import sift from 'sift';

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function ForbiddenError(message, options) {
  if (options === void 0) {
    options = {};
  }

  Error.call(this);
  this.constructor = ForbiddenError;
  this.subject = options.subject;
  this.subjectName = options.subjectName;
  this.action = options.action;
  this.field = options.field;
  this.message = message || "Cannot execute \"" + this.action + "\" on \"" + this.subjectName + "\"";

  if (typeof Error.captureStackTrace === 'function') {
    this.name = this.constructor.name;
    Error.captureStackTrace(this, this.constructor);
  } else {
    this.stack = new Error(this.message).stack;
  }
}
ForbiddenError.prototype = Object.create(Error.prototype);

function wrapArray(value) {
  return Array.isArray(value) ? value : [value];
}
function getSubjectName(subject) {
  if (!subject || typeof subject === 'string') {
    return subject;
  }

  var Type = typeof subject === 'object' ? subject.constructor : subject;
  return Type.modelName || Type.name;
}
function clone(object) {
  return JSON.parse(JSON.stringify(object));
}

var REGEXP_SPECIAL_CHARS = /[-/\\^$+?.()|[\]{}]/g;
var REGEXP_ANY = /\.?\*+\.?/g;
var REGEXP_STARS = /\*+/;
var REGEXP_DOT = /\./g;

function detectRegexpPattern(match, index, string) {
  var quantifier = string[0] === '*' || match[0] === '.' && match[match.length - 1] === '.' ? '+' : '*';
  var matcher = match.indexOf('**') === -1 ? '[^.]' : '.';
  var pattern = match.replace(REGEXP_DOT, '\\$&').replace(REGEXP_STARS, matcher + quantifier);
  return index + match.length === string.length ? "(?:" + pattern + ")?" : pattern;
}

function escapeRegexp(match, index, string) {
  if (match === '.' && (string[index - 1] === '*' || string[index + 1] === '*')) {
    return match;
  }

  return "\\" + match;
}

function createPattern(fields) {
  var patterns = fields.map(function (field) {
    return field.replace(REGEXP_SPECIAL_CHARS, escapeRegexp).replace(REGEXP_ANY, detectRegexpPattern);
  });
  var pattern = patterns.length > 1 ? "(?:" + patterns.join('|') + ")" : patterns[0];
  return new RegExp("^" + pattern + "$");
}

var Rule =
/*#__PURE__*/
function () {
  function Rule(params) {
    this.actions = params.actions || params.action;
    this.subject = params.subject;
    this.fields = !params.fields || params.fields.length === 0 ? undefined : wrapArray(params.fields);
    Object.defineProperty(this, '_fieldsPattern', {
      writable: true
    });
    this.inverted = !!params.inverted;
    this.conditions = params.conditions;
    Object.defineProperty(this, '_matches', {
      writable: true,
      value: this.conditions ? sift(this.conditions) : undefined
    });
    this.reason = params.reason;
  }

  var _proto = Rule.prototype;

  _proto.matches = function matches(object) {
    if (!this._matches) {
      return true;
    }

    if (typeof object === 'string') {
      return !this.inverted;
    }

    return this._matches(object);
  };

  _proto.isRelevantFor = function isRelevantFor(object, field) {
    if (!this.fields) {
      return true;
    }

    if (!field) {
      return !this.inverted;
    }

    return this.matchesField(field);
  };

  _proto.matchesField = function matchesField(field) {
    if (typeof this._fieldsPattern === 'undefined') {
      this._fieldsPattern = this.fields.join('').indexOf('*') === -1 ? null : createPattern(this.fields);
    }

    if (this._fieldsPattern === null || field.indexOf('*') !== -1) {
      return this.fields.indexOf(field) !== -1;
    }

    return this._fieldsPattern.test(field);
  };

  return Rule;
}();

var PRIVATE_FIELD = typeof Symbol !== 'undefined' ? Symbol('private') : "__" + Date.now();
var DEFAULT_ALIASES = {
  crud: ['create', 'read', 'update', 'delete']
};

function hasAction(action, actions) {
  return action === actions || Array.isArray(actions) && actions.indexOf(action) !== -1;
}

var Ability =
/*#__PURE__*/
function () {
  Ability.addAlias = function addAlias(alias, actions) {
    if (alias === 'manage' || hasAction('manage', actions)) {
      throw new Error('Cannot add alias for "manage" action because it represents any action');
    }

    if (hasAction(alias, actions)) {
      throw new Error("Attempt to alias action to itself: " + alias + " -> " + actions.toString());
    }

    DEFAULT_ALIASES[alias] = actions;
    return this;
  };

  function Ability(rules, _temp) {
    var _ref = _temp === void 0 ? {} : _temp,
        _ref$RuleType = _ref.RuleType,
        RuleType = _ref$RuleType === void 0 ? Rule : _ref$RuleType,
        _ref$subjectName = _ref.subjectName,
        subjectName = _ref$subjectName === void 0 ? getSubjectName : _ref$subjectName;

    this[PRIVATE_FIELD] = {
      RuleType: RuleType,
      subjectName: subjectName,
      originalRules: rules || [],
      hasPerFieldRules: false,
      indexedRules: Object.create(null),
      mergedRules: Object.create(null),
      events: {},
      aliases: clone(DEFAULT_ALIASES)
    };
    this.update(rules);
  }

  var _proto = Ability.prototype;

  _proto.update = function update(rules) {
    if (!Array.isArray(rules)) {
      return this;
    }

    var payload = {
      rules: rules,
      ability: this
    };
    this.emit('update', payload);
    this[PRIVATE_FIELD].originalRules = rules.slice(0);
    this[PRIVATE_FIELD].mergedRules = Object.create(null);
    var index = this.buildIndexFor(rules);

    if (process.env.NODE_ENV !== 'production' && index.isAllInverted && rules.length) {
      // eslint-disable-next-line
      console.warn('[casl]: Ability contains only inverted rules. That means user will not be able to do any actions. This will be changed to Error throw in the next major version');
    }

    this[PRIVATE_FIELD].indexedRules = index.rules;
    this[PRIVATE_FIELD].hasPerFieldRules = index.hasPerFieldRules;
    this.emit('updated', payload);
    return this;
  };

  _proto.buildIndexFor = function buildIndexFor(rules) {
    var indexedRules = Object.create(null);
    var RuleType = this[PRIVATE_FIELD].RuleType;
    var isAllInverted = true;
    var hasPerFieldRules = false;

    for (var i = 0; i < rules.length; i++) {
      var rule = new RuleType(rules[i]);
      var actions = this.expandActions(rule.actions);
      var subjects = wrapArray(rule.subject);
      var priority = rules.length - i - 1;
      isAllInverted = !!(isAllInverted && rule.inverted);

      if (!hasPerFieldRules && rule.fields) {
        hasPerFieldRules = true;
      }

      for (var k = 0; k < subjects.length; k++) {
        var subject = subjects[k];
        indexedRules[subject] = indexedRules[subject] || Object.create(null);

        for (var j = 0; j < actions.length; j++) {
          var action = actions[j];
          indexedRules[subject][action] = indexedRules[subject][action] || Object.create(null);
          indexedRules[subject][action][priority] = rule;
        }
      }
    }

    return {
      isAllInverted: isAllInverted,
      hasPerFieldRules: hasPerFieldRules,
      rules: indexedRules
    };
  };

  _proto.expandActions = function expandActions(rawActions) {
    var aliases = this[PRIVATE_FIELD].aliases;
    var actions = wrapArray(rawActions);
    var i = 0;

    while (i < actions.length) {
      var action = actions[i++];

      if (aliases.hasOwnProperty(action)) {
        actions = actions.concat(aliases[action]);
      }
    }

    return actions;
  };

  _proto.can = function can(action, subject, field) {
    if (field && typeof field !== 'string') {
      // eslint-disable-next-line
      throw new Error('Ability.can expects 3rd parameter to be a string. See https://stalniy.github.io/casl/abilities/2017/07/21/check-abilities.html#checking-fields for details');
    }

    var rule = this.relevantRuleFor(action, subject, field);
    return !!rule && !rule.inverted;
  };

  _proto.relevantRuleFor = function relevantRuleFor(action, subject, field) {
    var rules = this.rulesFor(action, subject, field);

    for (var i = 0; i < rules.length; i++) {
      if (rules[i].matches(subject)) {
        return rules[i];
      }
    }

    return null;
  };

  _proto.possibleRulesFor = function possibleRulesFor(action, subject) {
    var subjectName = this[PRIVATE_FIELD].subjectName(subject);
    var mergedRules = this[PRIVATE_FIELD].mergedRules;
    var key = subjectName + "_" + action;

    if (!mergedRules[key]) {
      mergedRules[key] = this.mergeRulesFor(action, subjectName);
    }

    return mergedRules[key];
  };

  _proto.mergeRulesFor = function mergeRulesFor(action, subjectName) {
    var indexedRules = this[PRIVATE_FIELD].indexedRules;
    var mergedRules = [subjectName, 'all'].reduce(function (rules, subjectType) {
      var subjectRules = indexedRules[subjectType];

      if (!subjectRules) {
        return rules;
      }

      return Object.assign(rules, subjectRules[action], subjectRules.manage);
    }, []); // TODO: think whether there is a better way to prioritize rules
    // or convert sparse array to regular one

    return mergedRules.filter(Boolean);
  };

  _proto.rulesFor = function rulesFor(action, subject, field) {
    var rules = this.possibleRulesFor(action, subject);

    if (!this[PRIVATE_FIELD].hasPerFieldRules) {
      return rules;
    }

    return rules.filter(function (rule) {
      return rule.isRelevantFor(subject, field);
    });
  };

  _proto.cannot = function cannot() {
    return !this.can.apply(this, arguments);
  };

  _proto.throwUnlessCan = function throwUnlessCan() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    var rule = this.relevantRuleFor.apply(this, args);

    if (!rule || rule.inverted) {
      var action = args[0],
          subject = args[1],
          field = args[2];
      var subjectName = this[PRIVATE_FIELD].subjectName(subject);
      throw new ForbiddenError(rule ? rule.reason : null, {
        action: action,
        subjectName: subjectName,
        subject: subject,
        field: field
      });
    }
  };

  _proto.on = function on(event, handler) {
    var events = this[PRIVATE_FIELD].events;
    var isAttached = true;

    if (!events[event]) {
      events[event] = [];
    }

    events[event].push(handler);
    return function () {
      if (isAttached) {
        var index = events[event].indexOf(handler);
        events[event].splice(index, 1);
        isAttached = false;
      }
    };
  };

  _proto.emit = function emit(event, payload) {
    var handlers = this[PRIVATE_FIELD].events[event];

    if (handlers) {
      handlers.slice(0).forEach(function (handler) {
        return handler(payload);
      });
    }
  };

  _createClass(Ability, [{
    key: "rules",
    get: function get() {
      return this[PRIVATE_FIELD].originalRules;
    }
  }]);

  return Ability;
}();

function isStringOrNonEmptyArray(value) {
  return ![].concat(value).some(function (item) {
    return typeof item !== 'string';
  });
}

function isObject(value) {
  return value && typeof value === 'object';
}

var RuleBuilder =
/*#__PURE__*/
function () {
  function RuleBuilder(rule) {
    this.rule = rule;
  }

  var _proto = RuleBuilder.prototype;

  _proto.because = function because(reason) {
    this.rule.reason = reason;
    return this;
  };

  return RuleBuilder;
}();
var AbilityBuilder =
/*#__PURE__*/
function () {
  AbilityBuilder.define = function define(params, dsl) {
    var options = typeof params === 'function' ? {} : params;
    var define = params === options ? dsl : params;
    var builder = new this(options);
    var result = define(builder.can.bind(builder), builder.cannot.bind(builder));

    var buildAbility = function buildAbility() {
      return new Ability(builder.rules, options);
    };

    return result && typeof result.then === 'function' ? result.then(buildAbility) : buildAbility();
  };

  AbilityBuilder.extract = function extract() {
    var builder = new this();
    return {
      can: builder.can.bind(builder),
      cannot: builder.cannot.bind(builder),
      rules: builder.rules
    };
  };

  function AbilityBuilder(_temp) {
    var _ref = _temp === void 0 ? {} : _temp,
        _ref$subjectName = _ref.subjectName,
        subjectName = _ref$subjectName === void 0 ? getSubjectName : _ref$subjectName;

    this.rules = [];
    this.subjectName = subjectName;
  }

  var _proto2 = AbilityBuilder.prototype;

  _proto2.can = function can(actions, subject, conditionsOrFields, conditions) {
    if (!isStringOrNonEmptyArray(actions)) {
      throw new TypeError('AbilityBuilder#can expects the first parameter to be an action or array of actions');
    }

    var subjectName = [].concat(subject).map(this.subjectName);

    if (!isStringOrNonEmptyArray(subjectName)) {
      throw new TypeError('AbilityBuilder#can expects the second argument to be a subject name/type or an array of subject names/types');
    }

    var rule = {
      actions: actions,
      subject: subjectName
    };

    if (Array.isArray(conditionsOrFields) || typeof conditionsOrFields === 'string') {
      rule.fields = conditionsOrFields;
    }

    if (isObject(conditions) || !rule.fields && isObject(conditionsOrFields)) {
      rule.conditions = conditions || conditionsOrFields;
    }

    this.rules.push(rule);
    return new RuleBuilder(rule);
  };

  _proto2.cannot = function cannot() {
    var builder = this.can.apply(this, arguments);
    builder.rule.inverted = true;
    return builder;
  };

  return AbilityBuilder;
}();

export { Ability, AbilityBuilder, ForbiddenError, Rule, RuleBuilder };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@casl/ability/dist/es5m/index.js
// module id = null
// module chunks = 